import FingerprintJS from "@fingerprintjs/fingerprintjs";
import * as Sentry from "@sentry/react";
import ReactGA from "react-ga4";
import { getGa4MeasurementId } from "@/lib/analytics/ga4";

export const DEVICE_ID_STORAGE_KEY = "keptscene-device-id";

/** Must match the backend's X-Device-Id validation. */
const DEVICE_ID_PATTERN = /^[A-Za-z0-9-]{1,64}$/;

function isValidDeviceId(id: unknown): id is string {
  return typeof id === "string" && DEVICE_ID_PATTERN.test(id);
}

/** Cached promise, not value, so concurrent callers share one resolution. */
let cachedPromise: Promise<string> | null = null;

/** Used only when no randomness source exists; keeps the API non-throwing. */
const LAST_RESORT_DEVICE_ID = "keptscene-unknown-device";

/** randomUUID → getRandomValues UUID v4 → constant. Never throws. */
function generateDeviceId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to the next strategy.
  }

  try {
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
      bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch {
    // Fall through to the constant.
  }

  return LAST_RESORT_DEVICE_ID;
}

/** Malformed or missing stored values are treated as absent so they get repaired. */
function readStoredDeviceId(): string | null {
  try {
    const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    return isValidDeviceId(existing) ? existing : null;
  } catch {
    return null; // Storage unavailable (private mode).
  }
}

/** Best-effort: a failed write must not fail resolution. */
function persistDeviceId(id: string): void {
  try {
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, id);
  } catch {
    // Ignore.
  }
}

/** stored → FingerprintJS visitorId → generated. */
async function resolveDeviceId(): Promise<string> {
  const stored = readStoredDeviceId();
  if (stored) return stored;

  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    if (isValidDeviceId(result.visitorId)) {
      persistDeviceId(result.visitorId);
      return result.visitorId;
    }
  } catch {
    // FingerprintJS blocked or unavailable.
  }

  const id = generateDeviceId();
  persistDeviceId(id);
  return id;
}

/** Stable for the app's lifetime. Memoized; never rejects. */
export function getDeviceId(): Promise<string> {
  if (!cachedPromise) {
    cachedPromise = resolveDeviceId().catch(() => generateDeviceId());
  }
  return cachedPromise;
}

/** Best-effort: monitoring failures must not break the app or the tagging gate. */
function tagObservers(id: string): void {
  try {
    if (Sentry.isInitialized()) {
      Sentry.setTag("deviceId", id);
      Sentry.setUser({ id });
    }
  } catch {
    // Ignore.
  }

  try {
    const analyticsEnabled = Boolean(getGa4MeasurementId());
    if (analyticsEnabled) {
      ReactGA.gtag("set", "user_properties", { device_id: id });
    }
  } catch {
    // Ignore.
  }
}

/** Resolves once the id has been applied to Sentry and GA4. Never rejects. */
let taggedPromise: Promise<void> | null = null;

export function whenDeviceTagged(): Promise<void> {
  if (!taggedPromise) {
    taggedPromise = (async () => {
      let id: string;
      try {
        id = await getDeviceId();
      } catch {
        id = generateDeviceId(); // getDeviceId never rejects; safety net.
      }
      tagObservers(id);
    })().catch(() => {
      // Belt-and-braces; the body above cannot reject.
    });
  }
  return taggedPromise;
}

/** Resolves the id, then tags Sentry and GA4. Never rejects. */
export function initDevice(): Promise<void> {
  return whenDeviceTagged();
}
